import { useState } from 'react';
import { FileText, Download, ExternalLink, Trash2, LayoutTemplate, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { useLocalization } from '@/hooks/useLocalization';

export interface ProjectDocument {
  id: string;
  name: string;
  file_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  starred: boolean;
  created_at: string;
  project_id: string | null;
  project_name: string | null;
  summary: string | null;
  summary_generated_at: string | null;
}

interface DocumentViewerModalProps {
  document: ProjectDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenOriginal: (doc: ProjectDocument) => void;
  onDownload: (doc: ProjectDocument) => void;
  onDelete: (doc: ProjectDocument) => void;
  onBrowseTemplates: () => void;
  onSummaryGenerated: (docId: string, summary: string, summaryGeneratedAt: string) => void;
}

function isSummarizable(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.endsWith('.txt') || lower.endsWith('.md');
}

function formatSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DocumentViewerModal = ({
  document: doc,
  open,
  onOpenChange,
  onOpenOriginal,
  onDownload,
  onDelete,
  onBrowseTemplates,
  onSummaryGenerated,
}: DocumentViewerModalProps) => {
  const { t } = useLocalization();
  const [generating, setGenerating] = useState(false);
  const { toast } = useToast();

  if (!doc) return null;

  const summarizable = isSummarizable(doc.name);

  const handleGenerateSummary = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('summarize-document', {
        body: { documentId: doc.id },
      });

      if (error) throw error;
      if (!data?.summary) throw new Error('No summary returned');

      onSummaryGenerated(doc.id, data.summary, data.summaryGeneratedAt);
    } catch (err) {
      toast({
        title: t('Could not generate summary'),
        description: err instanceof Error ? err.message : t('Unknown error'),
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            {doc.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-muted-foreground">{t('Size')}</p>
              <p className="font-medium text-foreground">{formatSize(doc.size_bytes)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t('Uploaded')}</p>
              <p className="font-medium text-foreground">
                {formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-muted-foreground">{t('Project')}</p>
              <p className="font-medium text-foreground">{doc.project_name ?? t('Unfiled')}</p>
            </div>
          </div>

          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-primary" />
                
                {t('AI Summary')}
              </p>
              {doc.summary_generated_at && (
                <span className="text-xs text-muted-foreground">
                  
                  {t('Generated')} {formatDistanceToNow(new Date(doc.summary_generated_at), { addSuffix: true })}
                </span>
              )}
            </div>

            {doc.summary && <p className="text-sm text-muted-foreground">{doc.summary}</p>}

            {summarizable ? (
              <Button size="sm" variant="outline" onClick={handleGenerateSummary} disabled={generating}>
                {generating ? (
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 me-2" />
                )}
                {generating ? t('Generating...') : doc.summary ? t('Regenerate') : t('Generate Summary')}
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button size="sm" variant="outline" disabled>
                      <Sparkles className="w-4 h-4 me-2" />
                      
                      {t('Generate Summary')}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{t('AI summary isn\'t available for this file type yet.')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onOpenOriginal(doc)}>
              <ExternalLink className="w-4 h-4 me-2" />
              
              {t('Open Original')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDownload(doc)}>
              <Download className="w-4 h-4 me-2" />
              
              {t('Download')}
            </Button>
            <Button size="sm" variant="outline" onClick={onBrowseTemplates}>
              <LayoutTemplate className="w-4 h-4 me-2" />
              
              {t('Browse Templates')}
            </Button>
          </div>
          <Button size="sm" variant="destructive" onClick={() => onDelete(doc)}>
            <Trash2 className="w-4 h-4 me-2" />
            
            {t('Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewerModal;
